// ---------------------------------------------------------------------------
// compliance-region.ja.ts — Japanese translations for all 7 compliance regions
// ---------------------------------------------------------------------------
import type { ComplianceRegionText } from './compliance-region.content';

export const COMPLIANCE_JA: Record<string, ComplianceRegionText> = {
  'eu-uk': {
    region: 'EU / 英国',
    title: 'EU・英国ホテルコンプライアンスソフトウェア',
    subtitle: '完全なGDPR準拠、EU付加価値税自動化、PSD2/SCA決済セキュリティ、ePrivacyクッキー同意をすべてのHotelStack.ioワークフローに統合。',
    features: [
      { title: 'GDPRデータ保護', description: '自動データ保持ポリシー、削除権ワークフロー、ゲストデータエクスポート（DSAR）、すべてのゲスト接点での同意管理。' },
      { title: 'EU付加価値税自動化', description: 'EU加盟27カ国および英国における宿泊・付帯サービスの複数税率VAT計算。' },
      { title: 'PSD2 / SCA準拠', description: 'オンライン決済の強力な顧客認証、3Dセキュア2.0統合、SCA免除処理。' },
      { title: 'プライバシーとクッキー同意', description: 'PECRおよびePrivacy準拠のクッキーバナー、詳細な同意追跡、アナリティクスオプトイン/オプトアウト。' },
      { title: '警察届出書', description: 'フランス、イタリア、スペイン、ポルトガル向け国別フォーマットでの警察届出書自動生成。' },
      { title: '多言語・多通貨対応', description: '12のヨーロッパ言語インターフェース。EUR、GBP、CHF、SEK、NOKでのリアルタイム為替レート請求。' },
    ],
    taxDetails: [
      { name: '標準VAT（英国）', rate: '20%', description: '宿泊およびほとんどのホテルサービスに適用。' },
      { name: '軽減税率（フランス）', rate: '10%', description: 'フランスの宿泊サービスに適用される軽減TVA税率。' },
      { name: '軽減税率（ドイツ）', rate: '7%', description: 'ドイツの短期宿泊に適用される軽減付加価値税率。' },
      { name: '軽減税率（スペイン）', rate: '10%', description: 'スペインの接客業に適用される軽減IVA税率。' },
      { name: '軽減税率（イタリア）', rate: '10%', description: 'イタリアの宿泊サービスに適用される軽減IVA税率。' },
      { name: '宿泊税（変動）', rate: '都市別', description: '宿泊税・滞在税 — 都市ごとの自動計算。' },
    ],
    guestRegistration: 'HotelStack.ioはEU/英国全域でゲスト登録を自動化します。フランス、イタリア、スペイン、ポルトガル向けの警察届出書を生成。チェックイン時にパスポートおよびIDスキャンで必要な身元情報を取得します。',
    dataProtection: 'GDPR第17条に基づく完全な削除権ワークフロー、第15条に基づくデータ主体アクセス要求、第30条に基づく処理活動の記録、データ保護影響評価。英国GDPR・EU GDPR二重準拠。',
    faqItems: [
      { question: 'HotelStack.ioはホテルのGDPR準拠をどのように処理しますか？', answer: '当プラットフォームは自動データ保持ポリシー、削除権、ゲストデータエクスポート、同意管理、処理記録を含みます。すべてのゲストデータはEUデータ居住オプション付きで暗号化されています。' },
      { question: 'HotelStack.ioはPSD2強力な認証に対応していますか？', answer: 'はい。すべてのオンライン決済は低リスク取引の免除処理付きでSCA準拠の3Dセキュア2.0を経由します。' },
      { question: 'EU各国のVAT税率はどのように管理されますか？', answer: 'HotelStack.ioはすべてのEU加盟国と英国の現行VAT税率データベースを自動更新で維持しています。' },
      { question: '複数国の警察届出書を生成できますか？', answer: 'はい。チェックインモジュールがフランス、イタリア、スペイン、ポルトガル向けの国別警察届出書を生成します。' },
    ],
  },
  'united-states': {
    region: 'アメリカ合衆国',
    title: '米国ホテル税務・コンプライアンスソフトウェア',
    subtitle: '州別税務自動化、ADAアクセシビリティ追跡、PCI DSSレベル1決済セキュリティ、全米管轄区域の宿泊税申告。',
    features: [
      { title: '州・地方税自動化', description: '全50州の州売上税、宿泊税、観光改善地区料の自動計算。' },
      { title: 'ADAアクセシビリティ準拠', description: 'ADA対応客室在庫追跡、アクセシビリティレポート、WCAG 2.1 AA準拠保証。' },
      { title: 'PCI DSSレベル1', description: 'トークン化カード保管、ポイントツーポイント暗号化、自動PCIコンプライアンスレポート。' },
      { title: '宿泊税申告', description: '市、郡、州当局向けの申告準備済み宿泊税申告書生成。' },
      { title: '労働法準拠', description: '州別残業規則、最低賃金追跡、労働組合シフトスケジューリング。' },
      { title: 'CCPA / プライバシー法', description: 'カリフォルニア消費者プライバシー法および各州プライバシー法準拠、オプトアウトおよびデータ削除ワークフロー。' },
    ],
    taxDetails: [
      { name: '州売上税', rate: '0% - 7.25%', description: '州により異なる。客室料金および課税サービスに適用。' },
      { name: '市宿泊税', rate: '2% - 15%', description: '客室収入に対する市レベルの一時宿泊税。' },
      { name: '郡税', rate: '0.5% - 5%', description: '該当する場合の郡レベルの宿泊課税。' },
      { name: '観光地区料', rate: '1% - 3%', description: '主要都市圏の観光改善地区料。' },
      { name: 'コンベンション料', rate: '$1 - $5/泊', description: 'コンベンションセンター資金のための定額1泊料金。' },
      { name: 'リゾートフィー開示', rate: '変動', description: 'FTCガイドラインに基づく義務的リゾートフィー開示。' },
    ],
    guestRegistration: '米国ホテルのゲスト登録は州別要件に基づきプロパティレベルで管理されます。HotelStack.ioはゲストID・車両情報を取得し、州のホスピタリティ規制に準拠した登録カードを生成します。',
    dataProtection: 'HotelStack.ioはCCPA（カリフォルニア）、CDPA（バージニア）、CPA（コロラド）、CTDPA（コネチカット）、UCPA（ユタ）に準拠。オプトアウトおよびデータ削除ワークフロー内蔵。SOC 2 Type II認証インフラストラクチャ。',
    faqItems: [
      { question: 'HotelStack.ioは州ごとに異なる税率をどのように処理しますか？', answer: '税務エンジンは全米管轄区域の州・地方税率の包括的データベースを自動更新で維持しています。' },
      { question: 'HotelStack.ioはADAコンプライアンス追跡に対応していますか？', answer: 'はい。ADA対応客室数、アクセシビリティ機能を追跡し、予約エンジンのWCAG 2.1 AA準拠を保証します。' },
      { question: 'PCI DSS準拠はどのように維持されますか？', answer: 'すべての決済カードデータは入力時にトークン化されます。年次監査によりPCI DSSレベル1認証を維持しています。' },
      { question: '宿泊税申告書を生成できますか？', answer: 'はい。HotelStack.ioは全米主要管轄区域向けの申告準備済み宿泊税申告書を生成します。' },
    ],
  },
  'uae-middle-east': {
    region: 'UAE / 中東',
    title: 'UAE・中東ホテルコンプライアンスソフトウェア',
    subtitle: '観光ディルハム手数料自動化、TRN請求書付き5% VAT、DTCMゲスト登録、GCC運営向けアラビア語RTLインターフェース。',
    features: [
      { title: '観光ディルハム手数料', description: 'ホテル星評価に基づく自動観光ディルハム計算（1部屋1泊7-20 AED）。DTCM準備済みレポート。' },
      { title: '5% VATとTRN請求書', description: 'FTA準拠の5% VAT計算、税務登録番号（TRN）付き。' },
      { title: 'DTCMゲスト登録', description: 'ドバイ観光・商務マーケティング局eServicesポータル向け自動ゲストデータ取得。' },
      { title: 'アラビア語RTLインターフェース', description: 'PMS全体およびゲスト予約エンジンの完全アラビア語RTLインターフェース。' },
      { title: 'PDPLデータ保護', description: 'UAE個人データ保護法準拠、処理記録および同意管理。' },
      { title: '市政準拠', description: 'アブダビ、シャルジャ、ラスアルハイマ観光局要件対応。' },
    ],
    taxDetails: [
      { name: 'UAE VAT', rate: '5%', description: '客室収入を含む課税供給に対する連邦税。' },
      { name: '観光ディルハム（5つ星）', rate: '20 AED/部屋/泊', description: 'ドバイ5つ星ホテルのプレミアム料金。' },
      { name: '観光ディルハム（4つ星）', rate: '15 AED/部屋/泊', description: 'ドバイ4つ星ホテルの標準料金。' },
      { name: '観光ディルハム（3つ星）', rate: '10 AED/部屋/泊', description: 'ドバイ3つ星ホテルの料金。' },
      { name: '観光ディルハム（1-2つ星）', rate: '7 AED/部屋/泊', description: 'ドバイ1-2つ星ホテルの基本料金。' },
      { name: '市政手数料', rate: '7%（ドバイ）', description: '客室収入に適用されるドバイ市政手数料。' },
    ],
    guestRegistration: 'HotelStack.ioはチェックイン時にUAE ID、パスポート、ビザ情報を取得しDTCM eServicesに送信します。ゲスト国籍追跡と観光統計を自動化。',
    dataProtection: '2021年連邦法令第45号（個人データ保護法）に準拠。処理記録、正当な利益評価、UAEデータセンターでのデータ居住オプション。',
    faqItems: [
      { question: 'HotelStack.ioはドバイの観光ディルハムをどのように計算しますか？', answer: 'ホテルの星評価に基づき正しい観光ディルハム料金を自動適用します。月次DTCMレポートはワンクリックで生成。' },
      { question: 'TRN付きVAT請求書に対応していますか？', answer: 'はい。すべての請求書にTRN、5% VATの内訳が含まれ、FTA要件に準拠しています。' },
      { question: 'アラビア語インターフェースは完全なRTL対応ですか？', answer: 'はい。PMS、予約エンジン、請求書、レポートを含む完全なアラビア語RTLインターフェースです。' },
      { question: 'ドバイ以外の首長国のコンプライアンスに対応していますか？', answer: 'はい。アブダビ観光文化局、シャルジャ観光局、ラスアルハイマ観光局の要件に対応しています。' },
    ],
  },
  'thailand-sea': {
    region: 'タイ / 東南アジア',
    title: 'タイ・東南アジアホテルコンプライアンスソフトウェア',
    subtitle: 'タイVAT 7%自動化、TM30外国人届出、PDPA準拠、TAT報告をHotelStack.ioワークフローに統合。',
    features: [
      { title: 'タイVAT 7%', description: '客室収入・サービスのVAT 7%自動計算、歳入局フォーマットの税務請求書生成。' },
      { title: 'TM30外国人届出', description: '入国管理局への外国人ゲストTM30届出自動化、24時間以内提出。' },
      { title: 'PDPA準拠', description: '2019年タイ個人情報保護法準拠、同意管理、データ主体権利ワークフロー。' },
      { title: 'TAT統計報告', description: 'タイ国政府観光庁フォーマットでの宿泊統計およびゲスト人口統計レポート。' },
      { title: 'タイ語インターフェース', description: 'PMS全体およびゲスト向けの完全タイ語ローカライゼーション。' },
      { title: '東南アジア拡張', description: 'ベトナム、マレーシア、インドネシア、フィリピンの税務・報告フレームワーク対応。' },
    ],
    taxDetails: [
      { name: 'タイVAT', rate: '7%', description: '客室料金およびホテルサービスに適用される付加価値税。' },
      { name: '特定事業税', rate: '3.3%', description: '該当する不動産取引に適用。' },
      { name: '源泉徴収税', rate: '1-5%', description: '法人間サービス支払いに適用される源泉徴収税。' },
      { name: '地方開発税', rate: '変動', description: 'タイ地方自治体の不動産・土地評価税。' },
      { name: '印紙税', rate: '変動', description: '契約書および法的文書に適用される印紙税。' },
      { name: '観光振興基金', rate: '提案中', description: 'タイ観光振興を支援するための提案税。' },
    ],
    guestRegistration: 'HotelStack.ioはチェックイン時にパスポートとビザのスキャンを自動化し、外国人ゲスト向けTM30届出データを入国管理局システムに直接送信します。タイ国籍者のIDカードスキャンにも対応。',
    dataProtection: '2019年タイ個人情報保護法（PDPA）に準拠。同意記録、データ主体アクセス要求、データ移転影響評価、および必要に応じたデータ保護責任者（DPO）任命サポート。',
    faqItems: [
      { question: 'HotelStack.ioはTM30届出をどのように処理しますか？', answer: '外国人ゲストのチェックイン時にTM30データを自動取得し、入国管理局システムに直接送信。24時間以内の提出期限を遵守します。' },
      { question: 'タイのVAT税務請求書に対応していますか？', answer: 'はい。すべての請求書は歳入局の要件に準拠したVAT 7%の内訳を含みます。' },
      { question: 'PDPAコンプライアンスはどのように管理されますか？', answer: '同意記録、データ主体権利ワークフロー、処理記録、DPOサポートを含む完全なPDPA準拠ツールキットです。' },
      { question: 'タイ以外の東南アジア諸国に対応していますか？', answer: 'はい。ベトナム、マレーシア、インドネシア、フィリピンの税務・報告フレームワークに対応しています。' },
    ],
  },
  'australia': {
    region: 'オーストラリア',
    title: 'オーストラリアホテルコンプライアンスソフトウェア',
    subtitle: 'GST 10%自動化、BAS申告、APR準拠ゲスト登録、オーストラリアプライバシー原則をHotelStack.ioに統合。',
    features: [
      { title: 'GST 10%自動化', description: '宿泊・サービスのGST計算自動化、タックスインボイスおよびBASレポート生成。' },
      { title: 'BAS申告', description: '事業活動報告書（BAS）の自動生成、ATO要件準拠。' },
      { title: 'ゲスト登録', description: 'オーストラリア連邦・州規制に準拠したゲスト身元確認と記録保持。' },
      { title: 'プライバシー法準拠', description: '1988年オーストラリアプライバシー法およびAPP準拠、データ侵害通知義務対応。' },
      { title: 'フェアワーク準拠', description: 'ホスピタリティ産業裁定、ペナルティレート計算、労務基準準拠。' },
      { title: '多州対応', description: 'NSW、VIC、QLD、SA、WA、TASの各州規制およびライセンス要件対応。' },
    ],
    taxDetails: [
      { name: 'GST', rate: '10%', description: '宿泊料金およびほとんどのホテルサービスに適用される物品サービス税。' },
      { name: 'ワイン平衡税', rate: '29%', description: 'ミニバーおよびレストランのワインに適用される平衡税。' },
      { name: 'フリンジベネフィット税', rate: '47%', description: '従業員宿泊手当に適用されるFBT。' },
      { name: '市議会税', rate: '変動', description: '地方自治体の不動産課税。' },
      { name: '土地税', rate: '変動', description: '閾値超過の商業不動産に適用される州土地税。' },
      { name: '観光税', rate: '変動', description: '主要観光地域での州・地方観光課税。' },
    ],
    guestRegistration: 'HotelStack.ioはオーストラリアの州・連邦要件に準拠したゲスト登録を自動化。パスポートスキャン、ビザ確認、オーストラリア運転免許証認証をチェックインワークフローに統合。',
    dataProtection: '1988年プライバシー法、オーストラリアプライバシー原則（APP）、通知義務データ侵害（NDB）制度に準拠。データ保持ポリシー、アクセス・訂正権、越境データ移転保護を実施。',
    faqItems: [
      { question: 'HotelStack.ioはオーストラリアのGSTをどのように処理しますか？', answer: '宿泊料金・サービスのGST 10%を自動計算し、ATO準拠のタックスインボイスおよびBAS要約を生成します。' },
      { question: 'BAS申告に対応していますか？', answer: 'はい。GST回収額、クレジット、調整額を要約したBAS準備済みレポートを生成します。' },
      { question: 'オーストラリアプライバシー法への準拠はどのように行われますか？', answer: 'ゲストデータ処理はすべてのAPPに準拠。データ侵害通知ワークフロー、アクセス要求処理、データ保持ポリシー自動化を含みます。' },
      { question: 'フェアワーク裁定の計算に対応していますか？', answer: 'はい。ホスピタリティ産業裁定のペナルティレート、残業、祝日手当を自動計算します。' },
    ],
  },
  'japan': {
    region: '日本',
    title: '日本ホテルコンプライアンスソフトウェア',
    subtitle: '消費税10%自動化、旅館業法準拠、宿泊税計算、インボイス制度対応をHotelStack.ioに統合。',
    features: [
      { title: '消費税10%', description: '宿泊・飲食サービスの消費税自動計算。軽減税率8%対応。インボイス制度準拠。' },
      { title: '旅館業法準拠', description: '旅館業法および各自治体条例に基づく営業許可・運営基準への準拠支援。' },
      { title: '宿泊税', description: '東京都、大阪府、京都市など自治体別宿泊税の自動計算・請求。' },
      { title: 'インボイス制度', description: '適格請求書発行事業者登録番号付きインボイス生成。2023年制度対応。' },
      { title: '外国人宿泊者名簿', description: '旅券番号・国籍等の外国人宿泊者情報記録、所轄警察署への届出支援。' },
      { title: '日本語インターフェース', description: 'PMS全体の完全日本語ローカライゼーション。日本式日付・通貨フォーマット対応。' },
    ],
    taxDetails: [
      { name: '消費税（標準）', rate: '10%', description: '宿泊料金およびほとんどのホテルサービスに適用。' },
      { name: '消費税（軽減税率）', rate: '8%', description: 'テイクアウト飲食料品に適用される軽減税率。' },
      { name: '宿泊税（東京都）', rate: '100-200円/泊', description: '宿泊料金に応じた東京都宿泊税。' },
      { name: '宿泊税（大阪府）', rate: '100-300円/泊', description: '宿泊料金に応じた大阪府宿泊税。' },
      { name: '宿泊税（京都市）', rate: '200-1000円/泊', description: '宿泊料金に応じた京都市宿泊税。' },
      { name: '入湯税', rate: '150円/人', description: '温泉施設利用に適用される入湯税。' },
    ],
    guestRegistration: 'HotelStack.ioは旅館業法に基づく宿泊者名簿作成を自動化。外国人ゲストの旅券・国籍情報を記録し、所轄警察署への届出データを生成。日本国籍者の本人確認にも対応。',
    dataProtection: '個人情報保護法（APPI）に準拠。個人データの安全管理措置、開示・訂正・利用停止請求対応、越境データ移転に関する同意取得ワークフローを実装。',
    faqItems: [
      { question: 'HotelStack.ioは日本の消費税をどのように処理しますか？', answer: '標準税率10%と軽減税率8%を自動計算し、インボイス制度準拠の適格請求書を生成します。' },
      { question: '自治体別の宿泊税に対応していますか？', answer: 'はい。東京都、大阪府、京都市など各自治体の宿泊税を料金帯に応じて自動計算・請求します。' },
      { question: '外国人宿泊者の届出はどのように行われますか？', answer: 'チェックイン時にパスポートスキャンで旅券番号・国籍等を取得し、警察届出用データを自動生成します。' },
      { question: 'インボイス制度に対応していますか？', answer: 'はい。適格請求書発行事業者登録番号付きの適格請求書を自動生成し、保存要件にも対応しています。' },
    ],
  },
  'india': {
    region: 'インド',
    title: 'インドホテルコンプライアンスソフトウェア',
    subtitle: 'GSTスラブベース課税、C-Form/FRRO届出、FHRAI報告、インドデータ保護法準拠をHotelStack.ioに統合。',
    features: [
      { title: 'GSTスラブベース課税', description: '客室料金帯別GST率自動適用（5%/12%/18%）。GSTR申告準備済みレポート生成。' },
      { title: 'C-Form届出', description: '外国人ゲストのC-Form（フォームC）自動生成、FRRO/FRO電子届出対応。' },
      { title: 'FHRAI報告', description: 'インドホテル・レストラン協会連盟フォーマットでの統計・運営レポート。' },
      { title: 'ヒンディー語インターフェース', description: 'PMS全体の完全ヒンディー語ローカライゼーション。多州言語サポート。' },
      { title: 'DPDPA準拠', description: '2023年デジタル個人データ保護法準拠、同意管理、データ処理記録。' },
      { title: '州別ライセンス', description: '各州の観光局・消防署・保健所・自治体ライセンス要件追跡。' },
    ],
    taxDetails: [
      { name: 'GST（7500ルピー以下）', rate: '12%', description: '1泊7,500ルピー以下の客室に適用されるGST率。' },
      { name: 'GST（7500ルピー超）', rate: '18%', description: '1泊7,500ルピー超の客室に適用されるGST率。' },
      { name: 'GST（飲食）', rate: '5%', description: 'エアコンなしレストランの飲食サービスに適用。' },
      { name: 'GST（飲食・AC）', rate: '5% ITC付', description: 'エアコン付きレストランの飲食サービスに適用。' },
      { name: 'TCS（源泉徴収税）', rate: '1%', description: '一定額超の取引に適用される源泉徴収税。' },
      { name: '地方税', rate: '変動', description: '州・市レベルの追加課税（贅沢税の代替）。' },
    ],
    guestRegistration: 'HotelStack.ioは外国人ゲストのC-Form自動生成、FRRO/FROへの電子届出を自動化。パスポート・ビザスキャン、12時間以内の届出期限遵守を支援。インド国民のID（Aadhaar/選挙人証）確認にも対応。',
    dataProtection: '2023年デジタル個人データ保護法（DPDPA）に準拠。同意取得・記録、データ主体権利ワークフロー、データ受託者義務の履行、越境データ移転に関する制限への対応。',
    faqItems: [
      { question: 'HotelStack.ioはインドのGSTスラブをどのように処理しますか？', answer: '客室料金帯に基づきGST率（12%/18%）を自動適用し、GSTR-1およびGSTR-3B申告準備済みレポートを生成します。' },
      { question: 'C-FormとFRRO届出に対応していますか？', answer: 'はい。外国人ゲストのチェックイン時にC-Formを自動生成し、FRRO/FROポータルへの電子届出を支援します。' },
      { question: 'DPDPA準拠はどのように管理されますか？', answer: '同意管理、データ主体権利ワークフロー、処理記録、データ受託者義務履行の完全なツールキットを提供します。' },
      { question: '州ごとのライセンス追跡に対応していますか？', answer: 'はい。各州の観光局・消防署・保健所・自治体のライセンス更新期限と要件を追跡します。' },
    ],
  },
};
